import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../../contexts/AuthContext';
import { formatPHP } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Boxes, Loader2, CheckCircle2, Snowflake, PackageCheck, Play, AlertTriangle,
  RefreshCw, Search, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import AdjHistoryPopover from '../../AdjHistoryPopover';
import ProductSearchAdd from '../../ProductSearchAdd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAPITAL_SOURCES = [
  { key: 'moving_average', label: 'Moving Average Cost' },
  { key: 'manual', label: 'Manual / Last PO Price' },
];

export default function StockCountPanel({ session, onSaved, onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDelta, setRefreshDelta] = useState(null);
  const [capitalSource, setCapitalSource] = useState('moving_average');
  const [counts, setCounts] = useState({});   // product_id -> { whole, loose, adj }
  const [search, setSearch] = useState('');
  const [searchMatch, setSearchMatch] = useState(null); // product_id of first match
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/stock-count`);
      setData(res.data);
      const seed = {};
      (res.data.count_sheet?.items || []).forEach((it) => {
        const adj = it.count_adjustment ?? 0;
        const rawWhole = it.actual_whole != null
          ? it.actual_whole - adj
          : (it.actual_quantity != null ? Math.trunc(it.actual_quantity) : '');
        seed[it.product_id] = {
          whole: rawWhole !== '' ? String(rawWhole) : '',
          loose: it.actual_loose != null ? String(it.actual_loose) : '',
          adj: adj !== 0 ? String(adj) : '',
        };
      });
      setCounts(seed);
    } catch { setData(null); }
    setLoading(false);
  }, [session.id]);

  useEffect(() => { load(); }, [load]);

  // ── Search / jump ────────────────────────────────────────────
  useEffect(() => {
    if (!search.trim()) { setSearchMatch(null); return; }
    const q = search.toLowerCase();
    const items = data?.count_sheet?.items || [];
    const found = items.find(it =>
      it.product_name.toLowerCase().includes(q) || (it.sku || '').toLowerCase().includes(q)
    );
    setSearchMatch(found?.product_id || null);
  }, [search, data]);

  const jumpTo = (pid) => {
    const el = rowRefs.current[pid];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-[#1A4D2E]', 'ring-offset-1');
    setTimeout(() => el.classList.remove('ring-2', 'ring-[#1A4D2E]', 'ring-offset-1'), 1800);
  };

  // ── Per-item blur save ───────────────────────────────────────
  const saveItem = useCallback(async (pid) => {
    const sheet = data?.count_sheet;
    if (!sheet) return;
    const it = (sheet.items || []).find(i => i.product_id === pid);
    if (!it) return;
    const c = counts[pid] || {};
    const hasInput = c.whole !== '' && c.whole != null;
    const hasLoose = c.loose !== '' && c.loose != null;
    if (!hasInput && !hasLoose) return;
    const adj = parseInt(c.adj || 0, 10) || 0;
    const finalWhole = Math.max(0, (parseInt(c.whole || 0, 10) || 0) + adj);
    const payload = it.has_repack
      ? { product_id: pid, actual_whole: finalWhole, actual_loose: parseInt(c.loose || 0, 10) || 0, count_adjustment: adj }
      : { product_id: pid, actual_quantity: finalWhole, count_adjustment: adj };
    try {
      await api.put(`${BACKEND_URL}/api/count-sheets/${data.count_sheet_id}/items`, { items: [payload] });
    } catch { toast.error('Auto-save failed — re-enter value'); }
  }, [data, counts]);

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/link-count-sheet`,
        { capital_price_source: capitalSource, audit_mode: true });
      setData(res.data);
      toast.success('Stock count sheet created — count blind, then submit');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the stock count');
    }
    setBusy(false);
  };

  const setCount = (pid, field, v) => {
    if (field === 'adj') {
      const clean = v.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
      setCounts((prev) => ({ ...prev, [pid]: { ...prev[pid], adj: clean } }));
    } else {
      const clean = v.replace(/[^\d]/g, '');
      setCounts((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: clean } }));
    }
  };

  const getFinal = (c) => {
    const whole = parseInt(c?.whole || 0, 10) || 0;
    const adj = parseInt(c?.adj || 0, 10) || 0;
    return Math.max(0, whole + adj);
  };

  const buildPayload = () => {
    const sheet = data?.count_sheet;
    const items = [];
    (sheet?.items || []).forEach((it) => {
      const c = counts[it.product_id] || {};
      const hasInput = c.whole !== '' && c.whole != null;
      const looseInput = c.loose !== '' && c.loose != null;
      if (!hasInput && !looseInput) return;
      const adj = parseInt(c.adj || 0, 10) || 0;
      const finalWhole = Math.max(0, (parseInt(c.whole || 0, 10) || 0) + adj);
      if (it.has_repack) {
        items.push({ product_id: it.product_id, actual_whole: finalWhole, actual_loose: parseInt(c.loose || 0, 10) || 0, count_adjustment: adj });
      } else {
        items.push({ product_id: it.product_id, actual_quantity: finalWhole, count_adjustment: adj });
      }
    });
    return items;
  };

  const saveProgress = async (silent = false) => {
    const items = buildPayload();
    if (items.length === 0) return true;
    try {
      await api.put(`${BACKEND_URL}/api/count-sheets/${data.count_sheet_id}/items`, { items });
      if (!silent) toast.success('Counts saved');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save counts');
      return false;
    }
  };

  const refreshExpected = async () => {
    setRefreshing(true);
    try {
      const ok = await saveProgress(true);
      if (!ok) { setRefreshing(false); return; }
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/refresh-stock-expected`, {});
      setRefreshDelta(res.data);
      await load();
      toast.success(res.data.message || 'Expected inventory refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refresh expected quantities');
    }
    setRefreshing(false);
  };

  const submit = async () => {
    setBusy(true);
    const ok = await saveProgress(true);
    if (!ok) { setBusy(false); return; }
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/complete-stock-count`, {});
      toast.success('Stock count submitted');
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finish counting every item before submitting');
    }
    setBusy(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>;
  }

  if (data && !data.frozen) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12" data-testid="stock-count-needs-freeze">
        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-3"><Snowflake size={20} className="text-amber-500" /></div>
        <p className="text-sm font-semibold text-slate-700">Freeze the snapshot first</p>
        <p className="text-xs text-slate-500 max-w-sm mt-1">The stock count compares physical inventory against the frozen expected. Freeze before counting.</p>
        <button onClick={() => onJump?.('freeze')} className="mt-3 text-sm font-semibold text-[#1A4D2E] hover:underline" data-testid="stock-count-goto-freeze">Go to Freeze →</button>
      </div>
    );
  }

  const sheet = data?.count_sheet;
  const status = data?.status;

  if (!sheet) {
    return (
      <div className="space-y-4" data-testid="stock-count-start">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-700 mb-1">Start a blind stock count</p>
          <p className="text-xs text-slate-500">We snapshot expected inventory at the freeze cutoff, then hide it so the count is unbiased. The variance appears in Review.</p>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Capital cost basis (for valuing variance)</label>
          <Select value={capitalSource} onValueChange={setCapitalSource}>
            <SelectTrigger className="h-10 w-64" data-testid="stock-capital-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CAPITAL_SOURCES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <Button onClick={start} disabled={busy} data-testid="stock-count-start-btn" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Play size={15} className="mr-1.5" />}Start Stock Count
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    const sm = sheet.summary || {};
    const net = sm.net_variance_capital || 0;
    return (
      <div className="space-y-4" data-testid="stock-count-done">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
          <PackageCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Stock count submitted</p>
            <p className="text-xs text-emerald-700 mt-0.5">Count sheet {sheet.count_sheet_number} · {sm.items_counted}/{sm.total_items} items counted.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-slate-200 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">With variance</p>
            <p className="text-sm font-bold text-slate-700 font-mono">{sm.items_with_variance || 0}</p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Shortage</p>
            <p className="text-sm font-bold text-red-600 font-mono">{sm.items_with_shortage || 0}</p>
          </div>
          <div className={`rounded-lg border p-3 text-center ${net < 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Net (capital)</p>
            <p className={`text-sm font-bold font-mono ${net < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{net > 0 ? '+' : ''}{formatPHP(net)}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── In progress ────────────────────────────────────────────
  const allItems = sheet.items || [];
  const isReadOnly = sheet.status === 'completed' || sheet.status === 'cancelled';

  const searchQ = search.toLowerCase().trim();
  const filteredItems = searchQ
    ? allItems.filter(it => it.product_name.toLowerCase().includes(searchQ) || (it.sku || '').toLowerCase().includes(searchQ))
    : allItems;

  const countedN = allItems.filter((it) => {
    const c = counts[it.product_id] || {};
    return (c.whole !== '' && c.whole != null) || (c.loose !== '' && c.loose != null);
  }).length;

  const hasRepacks = allItems.some(it => it.has_repack);

  return (
    <div className="space-y-3" data-testid="stock-count-grid">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{sheet.count_sheet_number} · blind count</span>
        <div className="flex items-center gap-2">
          {refreshDelta !== null && refreshDelta.changed > 0 && (
            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="refresh-delta-badge">
              +{refreshDelta.changed} expected updated
            </span>
          )}
          <span className="text-xs font-semibold text-slate-600" data-testid="stock-count-progress">{countedN}/{allItems.length} counted</span>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product or SKU…"
            className="pl-8 h-8 text-sm" data-testid="stock-search-input" />
        </div>
        {searchMatch && (
          <Button size="sm" variant="outline" onClick={() => jumpTo(searchMatch)}
            className="h-8 px-3 text-xs gap-1 shrink-0" data-testid="stock-search-jump">
            <ArrowDown size={12} />Jump
          </Button>
        )}
        {searchQ && filteredItems.length === 0 && (
          <span className="text-xs text-slate-400 self-center shrink-0">No match</span>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <span className="font-semibold text-slate-500">Adj:</span>
        <span>Found more? <span className="font-bold text-emerald-600">+2</span></span>
        <span>Over-counted? <span className="font-bold text-red-500">-1</span></span>
        <span className="ml-auto">Click the adj number for history</span>
      </div>

      {/* Grid */}
      <div className="rounded-lg border border-slate-200 overflow-hidden max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
            <tr className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-center px-2 py-2 w-20">Whole</th>
              {hasRepacks && <th className="text-center px-2 py-2 w-20">Loose</th>}
              <th className="text-center px-2 py-2 w-20"><span className="text-amber-600">± Adj</span></th>
              <th className="text-center px-2 py-2 w-20">Final</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {(searchQ ? filteredItems : allItems).map((it) => {
              const c = counts[it.product_id] || {};
              const adj = parseInt(c.adj || 0, 10) || 0;
              const final = getFinal(c);
              const hasAdj = (it.adj_entries?.length > 0) || adj !== 0;
              const hasCount = c.whole !== '' && c.whole != null;
              const isHighlighted = searchMatch === it.product_id;
              return (
                <tr key={it.product_id}
                  id={`stock-row-${it.product_id}`}
                  ref={el => rowRefs.current[it.product_id] = el}
                  data-testid={`stock-row-${it.product_id}`}
                  className={`transition-colors ${isHighlighted ? 'bg-yellow-50' : hasAdj ? 'bg-amber-50/40' : ''}`}>
                  <td className="px-3 py-1.5">
                    <p className="font-medium text-slate-700 leading-tight">{it.product_name}</p>
                    <p className="text-[10px] text-slate-400">{it.sku} · {it.category} · {it.unit}</p>
                    {it.manually_added && <span className="text-[9px] font-bold text-purple-500 uppercase">manually added</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <Input value={c.whole ?? ''} onChange={(e) => setCount(it.product_id, 'whole', e.target.value)}
                      onBlur={() => saveItem(it.product_id)}
                      inputMode="numeric" placeholder="0" className="h-8 w-20 text-center font-mono mx-auto"
                      data-testid={`stock-whole-${it.product_id}`} />
                  </td>
                  {hasRepacks && (
                    <td className="px-2 py-1.5 text-center">
                      {it.has_repack ? (
                        <Input value={c.loose ?? ''} onChange={(e) => setCount(it.product_id, 'loose', e.target.value)}
                          onBlur={() => saveItem(it.product_id)}
                          inputMode="numeric" placeholder="0" className="h-8 w-20 text-center font-mono mx-auto"
                          data-testid={`stock-loose-${it.product_id}`} />
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-center">
                    {!isReadOnly ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <Input
                          value={c.adj ?? ''}
                          onChange={(e) => setCount(it.product_id, 'adj', e.target.value)}
                          onBlur={() => saveItem(it.product_id)}
                          inputMode="text" placeholder="0"
                          className={`h-8 w-20 text-center font-mono mx-auto border-amber-300 ${
                            adj > 0 ? 'text-emerald-600 bg-emerald-50' : adj < 0 ? 'text-red-600 bg-red-50' : ''}`}
                          data-testid={`stock-adj-${it.product_id}`}
                        />
                        <AdjHistoryPopover
                          sheetId={data.count_sheet_id} productId={it.product_id}
                          productName={it.product_name}
                          entries={it.adj_entries || []} total={it.count_adjustment || 0}
                          readOnly={isReadOnly} onUpdate={load}
                        />
                      </div>
                    ) : (
                      <AdjHistoryPopover
                        sheetId={data.count_sheet_id} productId={it.product_id}
                        productName={it.product_name}
                        entries={it.adj_entries || []} total={it.count_adjustment || 0}
                        readOnly={true} onUpdate={load}
                      />
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`inline-block min-w-[48px] px-2 py-1 rounded-lg font-mono font-bold text-sm ${
                      !hasCount ? 'text-slate-300' :
                      hasAdj ? (adj > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700') :
                      'text-slate-700'}`}
                      data-testid={`stock-final-${it.product_id}`}>
                      {hasCount ? final : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add product */}
      <ProductSearchAdd
        sheetId={data.count_sheet_id}
        onAdded={load}
        onJumpTo={(pid) => { load().then(() => setTimeout(() => jumpTo(pid), 300)); }}
        disabled={isReadOnly}
      />

      {countedN < allItems.length && (
        <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle size={12} />Every item must be counted before you can submit.</p>
      )}
      <p className="text-xs text-slate-400 flex items-center gap-1">
        <RefreshCw size={11} />Late-encoded POs? <span className="font-semibold text-slate-500">Refresh Expected</span> updates expected quantities — your physical counts are preserved.
      </p>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <Button onClick={() => saveProgress(false)} disabled={busy || refreshing} variant="outline" data-testid="stock-count-save-btn" className="h-10 px-5">Save all</Button>
        <Button onClick={refreshExpected} disabled={busy || refreshing} variant="outline" data-testid="stock-count-refresh-btn"
          className="h-10 px-4 text-amber-700 border-amber-300 hover:bg-amber-50">
          {refreshing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}Refresh Expected
        </Button>
        <Button onClick={submit} disabled={busy || refreshing || countedN < allItems.length} data-testid="stock-count-submit-btn"
          className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
          {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Boxes size={15} className="mr-1.5" />}Submit Count
        </Button>
      </div>
    </div>
  );
}
